import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import dayjs from "dayjs";

// Custom hooks and utilities
import useForm from "../../hooks/useForm";
import { useThunk } from "../../hooks/useThunk";
import showToast from "../../utils/toastNotifications";
import convertUploadedFile from "../../utils/convertUploadedFile";

// Components
import FormSectionHeading from "../../components/Form/FormSectionHeading";
import CustomInput from "../../components/Form/Input";
import CustomeDatePicker from "../../components/Form/CustomDatePicker";
import CustomTimePicker from "../../components/Form/CustomTimePicker";
import ImageUpload from "../../components/Form/ImageUpload";
import CustomButton from "../../components/Form/CustomButton";
import Dropdown from "../../components/Form/Dropdown";
import MultiSelectDropdown from "../../components/Form/MultiSelectDropDown";

// Constants
import { COMMON_FIELD_TYPES } from "../../constants/fieldTypes";
import {
  categories,
  cities,
  firebaseUplaodFolders,
  FORM_MODES,
  maximumImagesUploadingSizes,
  referencesFormats,
} from "../../constants/commonConstants";
import {
  createJobPost,
  editJobPost,
  getSingleUserJobPost,
} from "../../store/thunks/jobPostThunks";
import Loader from "../../components/Common/Loader";
import ErrorDisplay from "../../error/ErrorDisplay";
import useFirebaseFileUpload from "../../hooks/useFirebaseFileUpload";
import PostJobSkeleton from "../../components/skeleton/PostJobSkeleton";
import convertTo24HourFormat from "../../utils/convertTo24HourFormat";

const PostJob = ({ mode }) => {
  const navigate = useNavigate();
  const { jobId } = useParams();

  // Redux thunks
  const [doCreateJobPost, isCreateJobPost, createJobPostError] =
    useThunk(createJobPost);
  const [
    doGetSingleUserJobPost,
    isGetSingleUserJobPost,
    getSingleUserJobPostError,
  ] = useThunk(getSingleUserJobPost);
  const [doEditJobPost, isEditJobPost, getEditJobPostError] =
    useThunk(editJobPost);

  // Firebase file upload hook
  const [uploadFilesToFirebase, isFileUploading, fileUploadError] =
    useFirebaseFileUpload();

  // Redux selectors
  const { posts: userJobPosts } = useSelector((state) => state.jobPost.myPosts);

  // Form validation and state management
  const {
    formData,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    validateForm,
    setTouched,
    clearError,
    setFormData,
    setErrors,
  } = useForm(
    {
      // Task Details
      title: "",
      description: "",
      date: "",
      time: "",
      // Task Category
      category: "",
      // Task Location
      location: "",
    },
    {
      title: true,
      description: true,
      date: true,
      time: true,
      category: true,
      location: true,
    },
    {
      title: COMMON_FIELD_TYPES.REQUIRED_FIELD,
      description: COMMON_FIELD_TYPES.REQUIRED_FIELD,
      date: COMMON_FIELD_TYPES.DATE_PICKER,
      time: COMMON_FIELD_TYPES.TIME_PICKER,
      category: {
        type: COMMON_FIELD_TYPES.DROPDOWN,
        displayKey: "name",
      },
      location: {
        type: COMMON_FIELD_TYPES.DROPDOWN,
        displayKey: "name",
      },
    }
  );

  // Component state
  const [uploadFiles, setUploadFiles] = useState([]);
  const [beforeChangeFiles, setBeforeChangeFiles] = useState([]);

  // Category state
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [skills, setSkills] = useState([]);
  const [skillError, setSkillError] = useState(false);
  const [skillTouched, setSkillTouched] = useState(false);

  // Location state
  const [selectedCity, setSelectedCity] = useState(null);
  const [subcities, setSubcities] = useState([]);
  const [subcityError, setSubcityError] = useState(false);
  const [subcityTouched, setSubcityTouched] = useState(false);

  // Transform data between form and server formats
  const transformJobPostData = useCallback((data, mode = "toServer") => {
    if (mode === "toServer") {
      // Transform FormData to Server Format
      const { taskCategory, taskLocation, images, ...rest } = data;

      // extract images urls from images
      const imageUrls = images.map((image) => image.url);

      return {
        ...rest,
        category: {
          categoryItem: taskCategory?.categoryName || "",
          skills: taskCategory?.skills || [],
        },
        location: {
          city: taskLocation?.cityName || "",
          subCities: taskLocation?.subcities || [],
        },
        images: imageUrls.length > 0 ? imageUrls : null,
      };
    } else if (mode === "toForm") {
      // Transform ServerData to FormData Format
      return {
        title: data?.title || "",
        description: data?.description || "",
        date: data?.date ? data?.date.split("T")[0] : "",
        time: data?.time || "",
        category: {
          categoryName: data?.category?.categoryItem || "",
          skills: data?.category?.skills || [],
        },
        location: {
          cityName: data?.location?.city || "",
          subcities: data?.location?.subCities || [],
        },
      };
    }

    return data;
  }, []);

  useEffect(() => {
    const isEditOrViewMode =
      mode === FORM_MODES.EDIT || mode === FORM_MODES.VIEW;

    // Only fetch job data in edit or view mode
    if (!isEditOrViewMode || !jobId) return;

    const initializeJobData = async () => {
      // Step 1: Get job post data (from store or API)
      let jobPostData;
      const existingJobPost = userJobPosts?.find((post) => post?._id === jobId);

      if (existingJobPost) {
        console.log("Using existing job post data from store");
        jobPostData = existingJobPost;
      } else {
        console.log("Fetching job post data from API");
        const result = await doGetSingleUserJobPost(jobId);

        if (result?.success) {
          jobPostData = result?.response?.data;
        } else if (result?.error) {
          showToast(
            "error",
            result?.error?.message || "Failed to fetch job post data"
          );
          return;
        }
      }

      // Step 2: Transform data to form format
      const transformedData = transformJobPostData(jobPostData, "toForm");

      // Step 3: Find category and city options that match data
      const foundCategory = categories.find(
        (category) => category.name === transformedData.category?.categoryName
      );

      const foundCity = cities.find(
        (city) => city.name === transformedData.location?.cityName
      );

      // Step 4: Set up skills and subcities
      let skillObjects = [];
      if (foundCategory && transformedData.category?.skills) {
        skillObjects = transformedData.category.skills
          .map((skillName) =>
            foundCategory.skills.find((s) => s.name === skillName)
          )
          .filter(Boolean);
      }

      let subcityObjects = [];
      if (foundCity && transformedData.location?.subcities) {
        subcityObjects = transformedData.location.subcities
          .map((subcityName) =>
            foundCity.subcities.find((s) => s.name === subcityName)
          )
          .filter(Boolean);
      }

      // Step 5: Set all state at once
      setSelectedCategory(foundCategory || null);
      setSelectedCity(foundCity || null);
      setSkills(skillObjects);
      setSubcities(subcityObjects);
      setFormData(transformedData);

      // Step 6: Handle files
      if (jobPostData?.images && jobPostData.images.length > 0) {
        const convertedFiles = jobPostData.images.map((file) =>
          convertUploadedFile(file)
        );
        setUploadFiles(convertedFiles);
        setBeforeChangeFiles(convertedFiles);
      }
    };

    initializeJobData();
  }, [
    jobId,
    mode,
    userJobPosts,
    doGetSingleUserJobPost,
    transformJobPostData,
    setFormData,
  ]);

  // Event Handlers
  const onFileUpload = (files) => {
    setUploadFiles([...files]);
  };

  const handleCategoryChange = useCallback(
    (selectedOption) => {
      setSelectedCategory(selectedOption);
      handleInputChange("category", selectedOption);
      setSkills([]);
      setSkillError(false);
      setSkillTouched(false);
    },
    [handleInputChange]
  );

  const handleSkillChange = useCallback((selectedOptions) => {
    setSkills(selectedOptions);
    setSkillError(selectedOptions.length === 0);
  }, []);

  const handleSkillBlur = useCallback(() => {
    setSkillTouched(true);
    setSkillError(skills.length === 0);
  }, [skills.length]);

  const handleCityChange = useCallback(
    (selectedOption) => {
      console.log("selectedOption", selectedOption);
      setSelectedCity(selectedOption);
      handleInputChange("location", selectedOption);
      setSubcities([]);
      setSubcityError(false);
      setSubcityTouched(false);
    },
    [handleInputChange]
  );

  const handleSubCityChange = useCallback((selectedOptions) => {
    setSubcities(selectedOptions);
    setSubcityError(selectedOptions.length === 0);
  }, []);

  const handleSubCityBlur = useCallback(() => {
    setSubcityTouched(true);
    setSubcityError(subcities.length === 0);
  }, [subcities.length]);

  // Handle date change
  const handleDateChange = useCallback(
    (date) => {
      if (date) {
        const formattedDate = date.format("YYYY-MM-DD");
        setFormData((prev) => ({
          ...prev,
          date: formattedDate,
        }));
        clearError("date");
      }
    },
    [clearError, setFormData]
  );

  // Handle time change
  const handleTimeChange = useCallback(
    (selectedTime) => {
      // Convert time to 24-hour format if it contains AM/PM
      const formattedTime = convertTo24HourFormat(selectedTime);

      setFormData((prev) => ({
        ...prev,
        time: formattedTime,
      }));

      clearError("time");
    },
    [clearError, setFormData]
  );

  // Form submission
  const handleSubmitPostJob = useCallback(
    async (e) => {
      e.preventDefault();

      // Set all fields as touched for validation
      setTouched({
        title: true,
        date: true,
        time: true,
        description: true,
        category: true,
        location: true,
      });

      // Validate custom fields
      setSkillTouched(true);
      setSubcityTouched(true);

      const skillsValid = selectedCategory && skills.length > 0;
      const subcitiesValid = selectedCity && subcities.length > 0;

      setSkillError(!skillsValid);
      setSubcityError(!subcitiesValid);

      // Check form validity
      const isFormValid = validateForm() && skillsValid && subcitiesValid;

      console.log('errors', errors);
      console.log('touched', touched);

      if (!isFormValid) {
        showToast("error", "Please fill in all required fields");
        return;
      }

      // NOTE: Check if date is today and time is in the past
      const now = dayjs();
      const selectedDate = dayjs(formData.date);
      const selectedTime = formData.time

      // Validate that the time is not empty
      if (!selectedTime) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          time: "Please select a time",
        }));
        return;
      }

      if (selectedDate.isSame(now, "day")) {
        // Make sure time is in 24-hour format for validation
        const timeIn24Format = convertTo24HourFormat(selectedTime);

        // Parse the hours and minutes from the time
        const [hours, minutes] = timeIn24Format.split(":").map(Number);
        const selectedDateTime = selectedDate.hour(hours).minute(minutes);

        if (selectedDateTime.isBefore(now)) {
          showToast("error", "Invalid Time: Please select a future time.");
          return;
        }
      }

      // Prepare data for submission
      const formDataToSubmit = {
        ...formData,
        taskCategory: {
          categoryName: selectedCategory.name,
          skills: skills.map((skill) => skill.name),
        },
        taskLocation: {
          cityName: selectedCity.name,
          subcities: subcities.map((subcity) => subcity.name),
        },
      };

      // Upload files to Firebase
      let imageUrls = [...beforeChangeFiles];

      // Identify added and removed files
      const addedFiles = uploadFiles.filter(
        (file) => !beforeChangeFiles.includes(file)
      );
      const removedFiles = beforeChangeFiles.filter(
        (file) => !uploadFiles.includes(file)
      );

      // Identify if main image has changed
      const mainImageChanged = beforeChangeFiles[0] !== uploadFiles[0];

      // If all images are removed, make imageUrls empty
      if (uploadFiles.length === 0) {
        imageUrls = [];
      } else if (addedFiles.length === 0 && removedFiles.length === 0) {
        imageUrls = [...uploadFiles]; // Just update the order
      } else {
        if (addedFiles.length > 0 || removedFiles.length > 0) {
          let imagesUploadResult = { success: false, uploadedUrls: [] };
          if (addedFiles.length > 0) {
            imagesUploadResult = await uploadFilesToFirebase(
              addedFiles,
              firebaseUplaodFolders.F_JOB_POSTS,
              maximumImagesUploadingSizes.size_450KB
            );

            if (!imagesUploadResult.success) {
              console.error("Upload Error:", imagesUploadResult.error);
              showToast("error", "Failed to upload images.");
              return;
            }
          }

          // convert response uploaded urls to file format
          const newUploadedUrls = imagesUploadResult?.uploadedUrls.map((url) =>
            convertUploadedFile(url)
          );

          // Merge new and old URLs
          imageUrls = imageUrls.filter((url) => !removedFiles.includes(url)); // Remove deleted files
          imageUrls.push(...newUploadedUrls); // Add new uploaded images

          // Ensure main image remains first
          if (mainImageChanged) {
            const newMainImage = newUploadedUrls[0] || uploadFiles[0];
            imageUrls = [
              newMainImage,
              ...imageUrls.filter((img) => img !== newMainImage),
            ];
          }
        }
      }

      // Update form data with new image URLs
      formDataToSubmit.images = imageUrls;

      const transformedData = transformJobPostData(
        formDataToSubmit,
        "toServer"
      );

      let result;

      if (mode === FORM_MODES.EDIT) {
        result = await doEditJobPost({
          jobData: transformedData,
          jobPostId: jobId,
        });
      } else {
        result = await doCreateJobPost(transformedData);
      }

      if (result?.success) {
        showToast("success", result?.response?.message);
        setTimeout(() => navigate("/client/my-posts"), 2000);
      } else {
        showToast("error", result?.error?.message || "Operation failed");
      }
    },
    [
      setTouched,
      validateForm,
      formData,
      selectedCategory,
      selectedCity,
      skills,
      subcities,
      transformJobPostData,
      mode,
      jobId,
      doEditJobPost,
      doCreateJobPost,
      navigate,
    ]
  );

  console.log("formData", formData);

  return (
    <div className=" px-[20px] md:px-[40px] lg:px-[100px] mt-12 mb-[88px] flex flex-col gap-10">
      <h1 className=" text-level-2 md:text-level-1 leading-[47.5px] font-nonito_sans font-bold text-primary text-center">
        {mode === FORM_MODES.EDIT ? "Edit Post" : "Post A Job"}
      </h1>

      {/* Show loader when in edit mode and data is being fetched */}
      {mode === FORM_MODES.EDIT && isGetSingleUserJobPost ? (
        <div className="bg-white rounded-[20px] border border-light-blue lg:mx-0 p-5 lg:p-9 flex flex-col items-center justify-center min-h-[400px]">
          <PostJobSkeleton />
        </div>
      ) : // Show error message when in edit mode and data fetching fails
      mode === FORM_MODES.EDIT && getSingleUserJobPostError ? (
        <ErrorDisplay
          errorMessage={
            getSingleUserJobPostError?.message || "An error occurred"
          }
          buttonText="Back to Previous Page"
          onButtonClick={() => {
            navigate(-1);
          }}
        />
      ) : (
        <div className="bg-white rounded-[20px] border border-light-blue lg:mx-0 p-5 lg:p-9 flex flex-col gap-12">
          {/* Task Details Section */}
          <div className="flex flex-col gap-6">
            <FormSectionHeading index={1} title="Task Details" />
            <div className="flex flex-col gap-6">
              <div className="flex flex-col md:flex-row gap-[30px]">
                <CustomInput
                  label="Task Title"
                  placeholder="Plumbing Repair and Electrical Checkup"
                  outerContainerStyle="flex-grow"
                  value={formData.title}
                  onChange={(e) => handleInputChange("title", e.target.value)}
                  onBlur={(e) => handleBlur("title", e.target.value)}
                  name="title"
                  error={touched.title && errors.title}
                  isRequired={true}
                  allowFillColor={false}
                  // className={`${formData.title ? "bg-[#E0F2F1] bg-opacity-[30%]" : ''} `}
                  disabled={mode === FORM_MODES.VIEW}
                />
                <CustomeDatePicker
                  id="date-picker"
                  label="Date"
                  value={formData.date ? dayjs(formData.date) : null}
                  onChange={handleDateChange}
                  onFocus={() => clearError("date")}
                  onBlur={() => handleBlur("date", formData.date)}
                  error={touched.date && errors.date}
                  placeholder="Date"
                  isRequired={false}
                  outerContainerStyle="w-full md:w-[264px]"
                  allowFillColor={false}
                  disabled={mode === FORM_MODES.VIEW}
                />

                <CustomTimePicker
                  id="time-picker"
                  label="Time"
                  selectedTime={formData.time || ""}
                  onChange={handleTimeChange}
                  onFocus={() => clearError("time")}
                  onBlur={() => handleBlur("time", formData.time)}
                  error={touched.time && errors.time}
                  placeholder="Time"
                  isRequired={false}
                  outerContainerStyle="w-full md:w-[264px]"
                  allowFillColor={false}
                  disabled={mode === FORM_MODES.VIEW}
                />
              </div>
              <CustomInput
                label="Task Description"
                placeholder="Enter a concise task"
                className="flex-grow"
                value={formData.description}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
                onBlur={(e) => handleBlur("description", e.target.value)}
                name="description"
                error={touched.description && errors.description}
                isRequired={true}
                allowFillColor={false}
                disabled={mode === FORM_MODES.VIEW}
              />
            </div>
          </div>

          {/* Task Category Section */}
          <div className="w-full flex gap-6 flex-col">
            <FormSectionHeading index={2} title="Task Category" />
            <div className="flex md:flex-row lg:gap-[30px] flex-col gap-8">
              <Dropdown
                options={categories}
                defaultOption={selectedCategory}
                placeholder="Select a Category"
                outerContainerStyle="w-full md:w-[400px]"
                isSearchable={true}
                label={"Category"}
                displayKey="name"
                idKey="_id"
                onSelect={handleCategoryChange}
                onFocus={() => clearError("category")} // Clear error when user interacts again
                onBlur={handleBlur}
                error={touched.category && errors.category}
                buttonClassName="rounded-lg"
                allowFillColor={false}
                disabled={mode === FORM_MODES.VIEW}
              />

              <MultiSelectDropdown
                options={selectedCategory?.skills || []}
                displayKey={"name"}
                defaultSelected={skills || []}
                idKey="_id"
                placeholder="Skill"
                outerContainerStyle="w-full "
                label={"Skill"}
                isSearchable={false}
                onSelect={handleSkillChange}
                onFocus={() => {
                  setSkillTouched(true);
                  setSkillError(false);
                }} // Set touched to true when user interacts
                onBlur={() => handleSkillBlur()}
                error={
                  skillTouched && skillError && "At least one skill is required"
                }
                disabled={!selectedCategory || mode === FORM_MODES.VIEW}
                buttonClassName="rounded-lg"
                allowFillColor={false}
              />
            </div>
          </div>

          {/* Task Location Section */}
          <div className={`w-full flex flex-col gap-6`}>
            <FormSectionHeading index={3} title="Task Location" />
            <div className="flex md:flex-row lg:gap-[30px] flex-col gap-8">
              <Dropdown
                options={cities}
                displayKey={"name"}
                defaultOption={selectedCity}
                idKey="_id"
                placeholder="City"
                outerContainerStyle="w-full md:w-[400px]"
                isSearchable={true}
                label={"City"}
                onSelect={handleCityChange}
                onFocus={() => {
                  setSubcityTouched(false);
                  setSubcityError(false);
                }}
                onBlur={handleBlur}
                error={touched.location && errors.location}
                buttonClassName="rounded-lg"
                allowFillColor={true}
                disabled={mode === FORM_MODES.VIEW}
              />
              <MultiSelectDropdown
                options={selectedCity?.subcities || []}
                displayKey={"name"}
                defaultSelected={subcities || []}
                idKey="_id"
                placeholder="Sub City"
                outerContainerStyle="w-full "
                label={"Sub City"}
                isSearchable={false}
                onSelect={handleSubCityChange}
                onFocus={() => {
                  setSubcityTouched(false);
                  setSubcityError(false);
                }}
                onBlur={handleSubCityBlur}
                error={
                  subcityTouched &&
                  subcityError &&
                  "At least one sub city is required"
                }
                disabled={!selectedCity || mode === FORM_MODES.VIEW}
                buttonClassName="rounded-lg"
                allowFillColor={true}
              />
            </div>
          </div>

          {/* Additional Requirement Section */}
          <div className="flex flex-col gap-4">
            <FormSectionHeading index={4} title="Additional Requirement" />
            <ImageUpload
              label="Images"
              uploadText="Jpeg, Jpg, png are allowed"
              accept={[referencesFormats.JPEG_JPG, referencesFormats.PNG]}
              onFileUpload={onFileUpload}
              uploadFiles={uploadFiles}
              isDisabled={mode === FORM_MODES.VIEW}
              allowImageArrange={true}
              numberOfFiles={5}
            />
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-12">
            <hr className="w-full border-[#cdd1de]" />
            <div className="flex flex-row gap-1 justify-end">
              <CustomButton
                onClick={handleSubmitPostJob}
                className="bg-primary text-white font-inter font-semibold text-sm h-[46px] w-[120px] flex items-center justify-center rounded-xl hover:bg-opacity-90 transition-colors"
                loaderColor="white"
                loading={isCreateJobPost || isEditJobPost || isFileUploading}
                buttonText={mode === FORM_MODES.EDIT ? "Save Post" : "Post Job"}
                disabled={
                  mode === FORM_MODES.VIEW ||
                  isCreateJobPost ||
                  isEditJobPost ||
                  isFileUploading
                }
              />

              <CustomButton
                buttonText="Cancel"
                onClick={() => navigate(-1)}
                className="text-primary font-inter h-[46px] w-[120px] flex items-center justify-center bg-white font-semibold text-sm hover:text-opacity-80 transition-colors"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PostJob;
